import React, {useCallback, useEffect, useRef, useState} from "react";
import {AutoSizer, List} from "react-virtualized";
import {IoIosWarning} from "react-icons/io";
import {Tooltip} from "antd";
import PDFPreviewControls from "./PDFPreviewControls";

const CrossCheckPdf = ({
                           searchParams,
                           comparedData,
                           comparedPlData,
                           contractData,
                           comparedInvoiceData,
                           comparedLcData,
                           comparedDiData,
                           comparedLabelData,
                           splitterData,
                           highlightedIndices,
                           extensionType,
                           setHighlightedIndices,
                           fileUrl,
                           pdfImages
                       }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [constructedKeyBol, setConstructedKeyBol] = useState(null);
    const [constructedKeyPl, setConstructedKeyPl] = useState(null);
    const [constructedKeyContract, setConstructedKeyContract] = useState(null);
    const [constructedKeyInvoice, setConstructedKeyInvoice] = useState(null);
    const [constructedKeyLc, setConstructedKeyLc] = useState(null);
    const [constructedKeyDi, setConstructedKeyDi] = useState(null);
    const [size, setSize] = useState({width: 0, height: 0});

    const viewerContainerRef = useRef(null);

    const documentIdPath = searchParams.get("document_id");
    const splitIdPath = parseInt(searchParams.get("split_id"));
    const documentTypePath = searchParams.get("doc_type");

    const pageNumber = splitterData[documentIdPath]?.[splitIdPath]?.traddocs_watermark_indices?.[currentPage];

    const [zoomLevelCompare, setZoomLevelCompare] = useState(1);
    const [rotationAngle, setRotationAngle] = useState(0);

    useEffect(() => {
        if (!documentIdPath || !documentTypePath || !comparedData) return;

        const findKeyInData = (data, setData) => {
            let keyFound = null;
            for (const key in data) {
                if (key.startsWith(`${documentIdPath}_`)) {
                    keyFound = key;
                    break;
                }
            }
            setData(keyFound);
        };

        findKeyInData(comparedData, setConstructedKeyBol);
        findKeyInData(comparedPlData, setConstructedKeyPl);
        findKeyInData(contractData, setConstructedKeyContract);
        findKeyInData(comparedInvoiceData, setConstructedKeyInvoice);
        findKeyInData(comparedLcData, setConstructedKeyLc);
        findKeyInData(comparedDiData, setConstructedKeyDi);
    }, [
        documentIdPath,
        splitIdPath,
        documentTypePath,
        comparedData,
        comparedPlData,
        comparedInvoiceData,
        comparedLcData,
        contractData,
        comparedDiData,
        comparedLabelData
    ]);

    const renderRow = useCallback(
        ({index, key, style}) => {
            const renderCombinedRectangle = (
                polygons,
                pageWidthInInches,
                pageHeightInInches,
                id,
                isDiscrepancy,
                item,
                index
            ) => {
                if (!polygons || polygons.length === 0) return null;

                let minX = Infinity,
                    minY = Infinity,
                    maxX = -Infinity,
                    maxY = -Infinity;

                const containerWidth = size.width
                const containerHeight = size.height

                polygons.forEach((polygon) => {
                    const [x1, y1, x2, y2, x3, y3, x4, y4] = polygon.map((coord, i) =>
                        i % 2 === 0
                            ? coord * containerWidth / pageWidthInInches
                            : coord * containerHeight / pageHeightInInches
                    );

                    minX = Math.min(minX, x1, x2, x3, x4);
                    minY = Math.min(minY, y1, y2, y3, y4);
                    maxX = Math.max(maxX, x1, x2, x3, x4);
                    maxY = Math.max(maxY, y1, y2, y3, y4);
                });

                const left = minX * zoomLevelCompare;
                const top = minY * zoomLevelCompare;
                const width = (maxX - minX) * zoomLevelCompare;
                const height = (maxY - minY) * zoomLevelCompare;

                const isHighlighted = highlightedIndices?.includes(id);

                function renderValue(value) {
                    if (Array.isArray(value)) {
                        return value.join(", "); // or return value.map((item, index) => <li key={index}>{item}</li>);
                    } else if (typeof value === "object" && value !== null) {
                        return JSON.stringify(value, null, 2);
                    } else if (typeof value === "string") {
                        return value;
                    } else {
                        return "N/A";
                    }
                }

                const tooltipContent = (
                    <div className="bg-white text-black px-3 py-3">
                        {item.item && (
                            <div className="font-semibold text-sm text-yellow-600 flex items-center space-x-2">
                                <IoIosWarning className="text-yellow-500 w-5 h-5"/>
                                <p>{item.item}</p>
                            </div>
                        )}
                        {item.discrepancy_detail && (
                            <div
                                className="rounded-lg text-gray-500 mt-2 text-xs">{renderValue(item.discrepancy_detail)}</div>
                        )}
                    </div>
                );

                const tooltipContent2 = (
                    <div className="bg-white text-black px-3 py-3">
                        {item.item && (
                            <div className="font-semibold text-sm text-green-500 flex items-center space-x-2">
                                <p>{item.item}</p>
                            </div>
                        )}
                        {item.discrepancy_detail && (
                            <div
                                className="rounded-lg text-gray-500 mt-2 text-xs">{renderValue(item.discrepancy_detail)}</div>
                        )}
                    </div>
                );

                return (
                    <Tooltip
                        color={isDiscrepancy ? "#eab308" : "rgb(34 197 94)"}
                        key={id}
                        title={isDiscrepancy ? tooltipContent : tooltipContent2}
                        overlayStyle={{maxWidth: "400px"}}
                        open={isHighlighted && true}
                    >
                        <div
                            className="transition absolute"
                            key={id}
                            style={{
                                left: `${left}px`,
                                top: `${top}px`,
                                width: `${width}px`,
                                height: `${height}px`,
                                borderWidth: isHighlighted && 2,
                                borderColor: isDiscrepancy ? "red" : "rgb(34 197 94)",
                                backgroundColor: isHighlighted ? "#eab30850" : "transparent",
                                zIndex: isHighlighted ? 999 : 1,
                            }}
                        />
                    </Tooltip>
                );
            };

            const renderPageRectangles = (pageNumber, type, comparedData, constructedKey) => {
                const rectangles = [];
                // Handle compared_a
                // if (comparedData[constructedKey]?.[splitIdPath].compared_a) {
                //   comparedData[constructedKey][splitIdPath].compared_a.forEach((item, index) => {
                //     if (item && item.coordinates) {
                //       item.coordinates.forEach((coord) => {
                //         if (coord && coord.page === pageNumber) {
                //           rectangles.push(
                //             renderCombinedRectangle(
                //               coord.polygons,
                //               coord.width,
                //               coord.height,
                //               item.id,
                //               item.discrepancy,
                //               item,
                //               index
                //             )
                //           );
                //         }
                //       });
                //     }
                //   });
                // }
                // Handle compared_b
                if (comparedData[constructedKey]?.[splitIdPath].compared_b) {
                    comparedData[constructedKey][splitIdPath].compared_b.forEach((item, index) => {
                        if (item.coordinates) {
                            const coord = item.coordinates[1];
                            if (coord && coord.page === pageNumber) {
                                rectangles.push(
                                    renderCombinedRectangle(
                                        coord.polygons,
                                        coord.width,
                                        coord.height,
                                        item.id,
                                        item.discrepancy,
                                        item,
                                        index
                                    )
                                );
                            }
                        }
                    });
                }
                return rectangles;
            };

            return (
                <div ref={viewerContainerRef} key={key} style={{width: '100%', height: '100%'}}
                     className={'flex justify-center overflow-y-auto overflow-x-hidden'}>
                    <div className={'relative'} onClick={(e) => e.stopPropagation()}
                         style={{height: window.innerHeight * zoomLevelCompare - 8 || 0}}>
                        {fileUrl && fileUrl.endsWith('pdf') ? pdfImages && (
                            <img onLoad={(e) => setSize({width: e.target.clientWidth, height: e.target.clientHeight})}
                                 className={'h-full object-contain'} src={pdfImages[pageNumber - 1]} alt={''}/>
                        ) : (<img onLoad={(e) => setSize({width: e.target.clientWidth, height: e.target.clientHeight})}
                                  className={'h-full object-contain'} src={fileUrl} alt={''}/>)}
                        {comparedData?.[constructedKeyBol]?.[splitIdPath]?.compared_b?.length > 0 &&
                            renderPageRectangles(pageNumber, "compared", comparedData, constructedKeyBol)}
                        {comparedPlData?.[constructedKeyPl]?.[splitIdPath]?.compared_b?.length > 0 &&
                            renderPageRectangles(pageNumber, "pl", comparedPlData, constructedKeyPl)}
                        {comparedLcData?.[constructedKeyLc]?.[splitIdPath]?.compared_b?.length > 0 &&
                            renderPageRectangles(pageNumber, "lc", comparedLcData, constructedKeyLc)}
                        {comparedDiData?.[constructedKeyDi]?.[splitIdPath]?.compared_b?.length > 0 &&
                            renderPageRectangles(pageNumber, "di", comparedDiData, constructedKeyDi)}
                        {comparedInvoiceData?.[constructedKeyInvoice]?.[splitIdPath]?.compared_b?.length > 0 &&
                            renderPageRectangles(pageNumber, "comparedInvoice", comparedInvoiceData, constructedKeyInvoice)}
                        {contractData?.[constructedKeyContract]?.[splitIdPath]?.compared_b?.length > 0 &&
                            renderPageRectangles(pageNumber, "contract", contractData, constructedKeyContract)}
                    </div>
                </div>
            );
        },
        [
            comparedData,
            comparedPlData,
            comparedInvoiceData,
            comparedLcData,
            comparedDiData,
            constructedKeyBol,
            constructedKeyPl,
            constructedKeyContract,
            constructedKeyInvoice,
            constructedKeyLc,
            constructedKeyDi,
            contractData,
            highlightedIndices,
            pageNumber,
            pdfImages,
            fileUrl,
            size,
            splitIdPath,
            zoomLevelCompare,
        ]
    );

    return (
        <div onClick={() => setHighlightedIndices([])}
             className="flex flex-col items-center overflow-hidden w-full h-full cursor-pointer">
            <div className="w-full h-full"
                 style={{transform: `rotate(${rotationAngle}deg)`, transformOrigin: "center"}}>
                <AutoSizer onResize={(size) => setSize((prevState) => ({width: prevState.width, height: size.height}))}>
                    {({height, width}) => (<List width={width}
                                                 height={height}
                                                 rowCount={1}
                                                 rowHeight={height}
                                                 rowRenderer={renderRow}/>)}
                </AutoSizer>
            </div>
            <div onClick={(e) => e.stopPropagation()}
                 className="flex justify-center fixed bottom-2 bg-black bg-opacity-50 rounded-lg">
                <PDFPreviewControls
                    zoomLevel={zoomLevelCompare}
                    setZoomLevel={setZoomLevelCompare}
                    rotationAngle={rotationAngle}
                    setRotationAngle={setRotationAngle}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    numPages={splitterData?.[documentIdPath]?.[splitIdPath]?.traddocs_watermark_indices.length}
                />
            </div>
        </div>
    );
};

export default CrossCheckPdf;
